import Phaser from 'phaser';
import { BaseScene } from '@core/scenes/BaseScene';
import { SceneKeys, Palette } from '@core/config/constants';
import { TextButton } from '@ui/components/TextButton';
import { CycleSelector } from '@ui/components/CycleSelector';
import {
  DressUpConfig,
  DRESS_COLORS,
  BACKGROUND_COLORS,
  ACCESSORIES,
} from '@modules/dressup/config';

const HEAD_EMOJI = '👧';

/**
 * Minijuego creativo de estilo: el jugador arma un look eligiendo color de vestido,
 * accesorio y fondo, y lo ve reflejado en el personaje en tiempo real. Al guardar el
 * look gana estrellas. Cuarto ejemplo del patron modular, con mecanica creativa
 * (sin perder), y reutiliza el componente CycleSelector.
 */
export class DressUpScene extends BaseScene {
  private dressIndex = 0;
  private accessoryIndex = 0;
  private backgroundIndex = 0;

  private backgroundRect!: Phaser.GameObjects.Rectangle;
  private dressGraphics!: Phaser.GameObjects.Graphics;
  private accessoryText!: Phaser.GameObjects.Text;
  private coinsText!: Phaser.GameObjects.Text;

  private characterX = 0;
  private characterY = 0;

  public constructor() {
    super(DressUpConfig.key);
  }

  public create(): void {
    this.initResponsive();
    this.resetState();
    this.characterX = this.scale.width * 0.32;
    this.characterY = this.scale.height * 0.52;

    this.backgroundRect = this.add.rectangle(
      this.centerX,
      this.centerY,
      this.scale.width,
      this.scale.height,
      BACKGROUND_COLORS[this.backgroundIndex].value,
    );

    this.add
      .text(this.centerX, 50, DressUpConfig.title, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '44px',
        color: Palette.textLight,
      })
      .setOrigin(0.5);

    this.coinsText = this.add
      .text(this.scale.width - 40, 40, '', {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '30px',
        color: Palette.textLight,
      })
      .setOrigin(1, 0);

    this.createCharacter();
    this.createSelectors();
    this.createButtons();
    this.redrawCharacter();
    this.refreshCoins();
  }

  private resetState(): void {
    this.dressIndex = 0;
    this.accessoryIndex = 0;
    this.backgroundIndex = 0;
  }

  private createCharacter(): void {
    this.dressGraphics = this.add.graphics();

    this.add
      .text(this.characterX, this.characterY - 60, HEAD_EMOJI, { fontSize: '120px' })
      .setOrigin(0.5);

    this.accessoryText = this.add
      .text(this.characterX, this.characterY - 150, '', { fontSize: '72px' })
      .setOrigin(0.5);
  }

  private createSelectors(): void {
    const selectorX = this.scale.width * 0.72;

    new CycleSelector(this, {
      x: selectorX,
      y: this.scale.height * 0.32,
      title: 'Vestido',
      labels: DRESS_COLORS.map((color) => color.name),
      onChange: (index: number) => {
        this.dressIndex = index;
        this.redrawCharacter();
      },
    });

    new CycleSelector(this, {
      x: selectorX,
      y: this.scale.height * 0.52,
      title: 'Accesorio',
      labels: ACCESSORIES.map((accessory) => accessory.name),
      onChange: (index: number) => {
        this.accessoryIndex = index;
        this.redrawCharacter();
      },
    });

    new CycleSelector(this, {
      x: selectorX,
      y: this.scale.height * 0.72,
      title: 'Fondo',
      labels: BACKGROUND_COLORS.map((color) => color.name),
      onChange: (index: number) => {
        this.backgroundIndex = index;
        this.redrawCharacter();
      },
    });
  }

  private createButtons(): void {
    new TextButton(this, {
      x: this.characterX,
      y: this.scale.height - 90,
      label: 'Guardar look ✨',
      color: Palette.primary,
      onClick: () => this.saveLook(),
    });

    new TextButton(this, {
      x: this.scale.width * 0.72,
      y: this.scale.height - 90,
      label: 'Volver al menu',
      color: Palette.accent,
      onClick: () => this.scene.start(SceneKeys.Hub),
    });
  }

  private redrawCharacter(): void {
    this.backgroundRect.setFillStyle(BACKGROUND_COLORS[this.backgroundIndex].value);
    this.accessoryText.setText(ACCESSORIES[this.accessoryIndex].emoji);
    this.drawDress(DRESS_COLORS[this.dressIndex].value);
  }

  private drawDress(color: number): void {
    const x = this.characterX;
    const y = this.characterY;

    this.dressGraphics.clear();
    this.dressGraphics.fillStyle(color, 1);

    // Torso
    this.dressGraphics.fillRoundedRect(x - 38, y + 6, 76, 70, 16);
    // Falda (triangulo)
    this.dressGraphics.fillTriangle(x - 95, y + 170, x + 95, y + 170, x, y + 30);
    // Cuello/detalle
    this.dressGraphics.fillCircle(x, y + 6, 22);
  }

  private saveLook(): void {
    this.profile.addCoins(DressUpConfig.coinsPerLook);
    this.audio.playReward();
    this.refreshCoins();
    this.showSavedFeedback();
  }

  private showSavedFeedback(): void {
    const message = this.add
      .text(this.characterX, this.characterY - 240, `¡Look guardado! +${DressUpConfig.coinsPerLook} ⭐`, {
        fontFamily: 'Trebuchet MS, sans-serif',
        fontSize: '30px',
        color: '#ffffff',
        backgroundColor: '#00000055',
        padding: { x: 16, y: 8 },
      })
      .setOrigin(0.5);

    this.tweens.add({
      targets: message,
      y: message.y - 60,
      alpha: 0,
      duration: 1200,
      ease: 'Cubic.easeOut',
      onComplete: () => message.destroy(),
    });
  }

  private refreshCoins(): void {
    this.coinsText.setText(`⭐ ${this.profile.coins}`);
  }
}
